import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useApproveRefund, useRefundsList } from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";
import { statusRefund } from "@/lib/status";

const TABS = [
	{ key: undefined as string | undefined, label: "Tous" },
	{ key: "PENDING_APPROVAL", label: "À approuver" },
	{ key: "APPROVED", label: "Approuvés" },
	{ key: "SUCCESS", label: "Effectués" },
	{ key: "FAILED", label: "Échecs" },
];

export function AdminRefundsPage() {
	const navigate = useNavigate();
	const [tab, setTab] = useState<string | undefined>(undefined);
	const { data, refetch, isLoading } = useRefundsList(tab);
	const approve = useApproveRefund();

	const items = data ?? [];

	return (
		<>
			<PageHead
				title="Remboursements"
				sub="Workflow 4-eyes selon montant (50 k / 500 k FCFA)"
				actions={
					<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
						<AdminIcon n="chevL" s={14} />
						Retour
					</AdminButton>
				}
			/>

			<Card>
				<div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
					<div
						className="filter-bar"
						style={{ marginBottom: 0, flexWrap: "wrap", gap: 6 }}
					>
						{TABS.map((t) => (
							<ChipFilter
								key={t.key ?? "all"}
								on={tab === t.key}
								onClick={() => setTab(t.key)}
							>
								{t.label}
							</ChipFilter>
						))}
					</div>
				</div>
				{isLoading ? (
					<CardBody>
						<div className="empty">Chargement…</div>
					</CardBody>
				) : items.length === 0 ? (
					<CardBody>
						<div className="empty">Aucun remboursement avec ce filtre.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Référence</th>
									<th>Paiement</th>
									<th className="right">Montant</th>
									<th>Motif</th>
									<th>Statut</th>
									<th>Demandé</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{items.map((r) => {
									const s = statusRefund(r.status);
									return (
										<tr key={r.id}>
											<td className="mono">{r.reference}</td>
											<td>
												<button
													type="button"
													onClick={() =>
														r.payment && navigate(`/paiements/${r.payment.id}`)
													}
													style={{
														background: "transparent",
														border: "none",
														padding: 0,
														fontFamily: "var(--a-font-mono, monospace)",
														color: "var(--a-terracotta)",
														cursor: "pointer",
													}}
												>
													{r.payment?.reference ?? r.paymentId.slice(0, 8)}
												</button>
											</td>
											<td className="right tn">
												<strong>{fmtFCFA(r.amount)}</strong>
											</td>
											<td>{r.reasonCode}</td>
											<td>
												<Tag variant={s.cls}>{s.l}</Tag>
											</td>
											<td className="muted">
												{new Date(r.requestedAt).toLocaleString("fr-FR")}
											</td>
											<td>
												{r.status === "PENDING_APPROVAL" && (
													<AdminButton
														variant="primary"
														onClick={() =>
															approve.mutate(r.id, {
																onSuccess: () => refetch(),
																onError: (e) => {
																	const err = e as {
																		response?: { data?: { message?: string } };
																		message: string;
																	};
																	alert(
																		err.response?.data?.message ?? err.message,
																	);
																},
															})
														}
														disabled={approve.isPending}
													>
														<AdminIcon n="check" s={12} />
														Approuver
													</AdminButton>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	);
}
